import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Eye, Send } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/components/common/Pagination';
import { EmptyState } from '@/components/common/EmptyState';
import { useDispatchesList } from '@/hooks/useDispatches';
import type { Dispatch, DispatchStatus } from '@/types/api.types';
import type { DispatchFilters } from '@/hooks/useDispatches';

const PAGE_SIZE = 20;

const STATUS_LABEL: Record<DispatchStatus, string> = {
  pending: 'Pendente',
  sent: 'Enviado',
  delivered: 'Entregue',
  read: 'Lido',
  failed: 'Falhou',
};

const STATUS_VARIANT: Record<DispatchStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'outline',
  sent: 'secondary',
  delivered: 'default',
  read: 'default',
  failed: 'destructive',
};

function TableSkeleton() {
  return (
    <div className="space-y-3 p-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 flex-1" />
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-8 w-8" />
        </div>
      ))}
    </div>
  );
}

interface Props {
  filters: DispatchFilters;
  onPageChange: (page: number) => void;
  onViewDetail: (d: Dispatch) => void;
}

export function DispatchesTable({ filters, onPageChange, onViewDetail }: Props) {
  const { data, isLoading } = useDispatchesList({ ...filters, pageSize: PAGE_SIZE });
  const dispatches = data?.data ?? [];
  const meta = data?.meta;

  if (isLoading) return <TableSkeleton />;

  if (dispatches.length === 0) {
    return <EmptyState icon={Send} title="Nenhum disparo encontrado" />;
  }

  return (
    <div className="space-y-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Destinatário</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Campanha</TableHead>
            <TableHead>Enviado em</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {dispatches.map((d) => (
            <TableRow key={d.id}>
              <TableCell>
                <p className="font-mono text-xs">{d.contactPhone}</p>
                {d.contactName && (
                  <p className="text-xs text-muted-foreground">{d.contactName}</p>
                )}
              </TableCell>
              <TableCell>
                <Badge variant={STATUS_VARIANT[d.status]}>{STATUS_LABEL[d.status]}</Badge>
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {d.campaignId ? (
                  <span className="font-mono">{d.campaignId.slice(0, 8)}…</span>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {d.sentAt
                  ? format(new Date(d.sentAt), "dd/MM 'às' HH:mm", { locale: ptBR })
                  : '—'}
              </TableCell>
              <TableCell>
                <Button variant="ghost" size="icon" onClick={() => onViewDetail(d)}>
                  <Eye className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {meta && (
        <Pagination
          page={filters.page ?? 1}
          totalPages={meta.totalPages}
          total={meta.total}
          pageSize={PAGE_SIZE}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
